import { useEffect, useCallback } from "react"
import { createPortal } from "react-dom"

import { PdfDataProvider, usePdfDataContext } from "@/app/export/pdf/contexts/PdfDataContext"
import ReportCanvas from "@/app/report/pdf/builder/components/ReportCanvas"
import { usePdfBuilderStore } from "@/stores/pdfBuilder"

import "./quickExportPdfDialog.scss"

/**
 * Inner component: PdfDataProvider 안에서 로딩 상태를 감지하여 부모에게 알림.
 */
function DataReadyBridge({ onDataReady }: { onDataReady: (ready: boolean) => void }) {
  const { isLoading } = usePdfDataContext()

  useEffect(() => {
    onDataReady(!isLoading)
  }, [isLoading, onDataReady])

  return null
}

interface QuickPdfOffscreenRendererProps {
  active: boolean
  onDataReady: (ready: boolean) => void
}

/**
 * 오프스크린 렌더러 — 다이얼로그 없이 Portal로 body에 PdfDataProvider + ReportCanvas를 렌더.
 * html2canvas가 캡처할 수 있도록 실제 DOM에 존재하되 화면 밖에 위치.
 */
export default function QuickPdfOffscreenRenderer({
  active,
  onDataReady,
}: QuickPdfOffscreenRendererProps) {
  const currentPageId = usePdfBuilderStore((s) => s.currentPageId)
  const orientation = usePdfBuilderStore(
    (s) => s.pages.find((p) => p.id === s.currentPageId)?.config.orientation ?? "portrait",
  )

  const canvasWidth = orientation === "landscape" ? 1123 : 794

  // 안정적인 콜백 — active가 아닐 때는 무시
  const handleDataReady = useCallback(
    (ready: boolean) => {
      if (active) onDataReady(ready)
    },
    [active, onDataReady],
  )

  if (!active) return null

  return createPortal(
    <div
      className="QuickExportPdfDialog__offscreen"
      style={{ width: canvasWidth }}
      aria-hidden="true">
      <PdfDataProvider>
        <DataReadyBridge onDataReady={handleDataReady} />
        {currentPageId && <ReportCanvas />}
      </PdfDataProvider>
    </div>,
    document.body,
  )
}
